import * as React from "react";
import { FC, useEffect, useState } from "react";
import { AckModelForm, asSpecs, FormModes } from "../common/AckModelForm";
import { useResourceApi } from "./AckResourceApi";
import { withPrivateRoute } from "../security/AckAuth";
import { AckErrorMessage, AckLoader, formatCurrency, useEffectLoader, useLoader } from "../common/AckUI";
import { AckTable, AckTableRowProps } from "../common/AckTable";
import { Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

/** Props for the AckResource component. */
export type EmbeddedProps = {
  /** The API prefix path for the resource. */
  apiPath: string;
  /** The name of the resource. */
  name: string;
  /** The presentation options for listing resources. */
  listOptions?: ListOptions;
}

/** Props for the list AckResource component. */
export type ListOptions = {
  /** The label for the group list field. */
  groupFieldLabel: string;
  /** The name of the field to group by. */
  groupFieldName: string;
  /** The flag how values should be sorted. */
  sortMode?: string;
  /** The transformation logic that needs to be applied to the returned values. */
  transformSpec?: string;
  /** The options controlling the presentation of the stats field. */
  statsOptions?: StatsOptions;
}

/** Props for the stats AckResource component. */
export type StatsOptions = {
  /** The label for the stats field. */
  statsFieldName: string;
  /** The flag controlling whether to show the sum of the stats field. */
  showSum?: boolean;
}

/** The result from the group query. */
export type GroupResult = {
  /** The value of the field that was grouped by. */
  groupFieldValue: string;
  /** The resulting transform value that will be used as the option label. */
  transformValue: string;
}

/** Type alias for resource changes. */
export type ChangeListener = OnResourceChangeListener;

/** Listener callbacks for resource changes. */
export type OnResourceChangeListener = {
  /** Called before a resource is created. */
  beforeCreate?: (payload: any) => Promise<any>;
  /** Called before a resource is edited. */
  beforeEdit?: (payload: any) => Promise<any>;
  /** Called before a resource is deleted. */
  beforeDelete?: (id: string) => Promise<void>;
}

/** The different presentation modes of resources. */
export enum Type {
  VIEW = "view",
  EDIT = "edit",
  CREATE = "create",
  LIST = "list",
}

const createPath = (path: string, regex: RegExp = new RegExp("/$")) => {
  return path.replace(regex, "") + `/create`;
};
const editPath = (path: string, id: string, regex: RegExp = new RegExp("/$")) => {
  return path.replace(regex, "") + `/${id}/edit`;
};
const viewPath = (path: string, id: string, regex: RegExp = new RegExp("/$")) => {
  return path.replace(regex, "") + `/${id}/view`;
};
const listPath = (path: string, regex: RegExp) => path.replace(regex, "");

const createListTableActions = (onView: (id: string) => void, onEdit: (id: string) => void, onDelete: (id: string) => void) => {
  const actions: FC<AckTableRowProps> = (rowProps) => {
    const onViewClick = () => onView(rowProps.payload["id"]);
    const onEditClick = () => onEdit(rowProps.payload["id"]);
    const onDeleteClick = () => onDelete(rowProps.payload["id"]);
    return (
      <th className="controls is-4" style={{ width: "220px" }}>
        <div className="buttons are-small">
          <button className="button is-light" onClick={onViewClick}>View</button>
          <button className="button is-light" onClick={onEditClick}>Edit</button>
          <button className="button is-danger is-outlined" onClick={onDeleteClick}>Delete</button>
        </div>
      </th>
    );
  };
  return actions;
};

const Empty = () => {
  return (
    <div className="columns">
      <div className="column">
        <h2 className="subtitle is-size-6">No results yet.</h2>
      </div>
    </div>
  );
};

/** The component rendering a list of resources. */
export const EmbeddedListResource: FC<EmbeddedProps & ChangeListener> = (props) => {
  const [spec, setSpec] = useState([] as any[]);
  const [values, setValues] = useState([] as any[]);
  const loader = useLoader();
  const api = useResourceApi(props.apiPath);
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToCreate = () => navigate(createPath(location.pathname));
  const navigateToEdit = (id: string) => navigate(editPath(location.pathname, id));
  const navigateToView = (id: string) => navigate(viewPath(location.pathname, id));

  const onDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      loader.load(async () => {
        if (props.beforeDelete) {
          await props?.beforeDelete(id);
        }
        await api.deleteValue(props.name, id);
        const values = await api.getValues(props.name);
        setValues(values);
      });
    }
  };

  const onLoad = async (context: any) => {
    const spec = await api.getSpec(props.name);
    const values = await api.getValues(props.name);
    if (context.isMounted) {
      setSpec(spec);
      setValues(values);
    }
  };

  useEffectLoader(loader, onLoad, [props]);

  const Content = () => {
    const rows = values.map((value: any) => ({ spec: asSpecs(spec), payload: value }));
    const actions = createListTableActions(navigateToView, navigateToEdit, onDelete);
    return (<AckTable rows={rows} actions={actions}/>);
  };

  return (
    <>
      {loader.errors && loader.errors.length > 0 && <AckErrorMessage errors={loader.errors}/>}
      <div className="buttons are-small">
        <button className="button is-primary is-small" onClick={navigateToCreate}>Create</button>
      </div>
      <AckLoader isLoading={loader.isLoading}/>
      {!loader.isLoading && values.length > 0 && <Content/>}
      {!loader.isLoading && values.length === 0 && <Empty/>}
    </>
  );
};

/** The component rendering a grouped list of resources. */
export const EmbeddedGroupListResource: FC<EmbeddedProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState([] as any[]);
  const [selectedGroupFieldValue, setSelectedGroupFieldValue] = useState(searchParams.get("p") as string | undefined);
  const [values, setValues] = useState([] as any[]);
  const [stats, setStats] = useState(undefined as any);
  const [spec, setSpec] = useState([] as any[]);
  const loader = useLoader();
  const api = useResourceApi(props.apiPath);
  const navigate = useNavigate();
  const location = useLocation();

  const groupFieldLabel = props.listOptions!.groupFieldLabel!;
  const groupFieldName = props.listOptions!.groupFieldName!;
  const sortMode = props.listOptions?.sortMode;
  const transformSpec = props.listOptions!.transformSpec;
  const statsOptions = props.listOptions?.statsOptions;

  const navigateToCreate = () => navigate(createPath(location.pathname));
  const navigateToEdit = (id: string) => navigate(editPath(location.pathname, id));
  const navigateToView = (id: string) => navigate(viewPath(location.pathname, id));

  const onDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      loader.load(async () => {
        await api.deleteValue(props.name, id);
        if (selectedGroupFieldValue) {
          await loadValues(selectedGroupFieldValue);
        }
      });
    }
  };

  const onLoad = async (context: any) => {
    const spec = await api.getSpec(props.name);
    const unsortedOptions = await api.getGroupValues(props.name, groupFieldName, transformSpec) as GroupResult[];
    const options = sortMode === "DESC"
      ? unsortedOptions.sort((a, b) => b.transformValue.localeCompare(a.transformValue))
      : unsortedOptions.sort((a, b) => a.transformValue.localeCompare(b.transformValue));
    const firstOption = options.length > 0 ? options[0] : undefined;
    if (context.isMounted) {
      setSpec(spec);
      setOptions(options);
    }
    const selectedValue = selectedGroupFieldValue ? selectedGroupFieldValue : firstOption?.groupFieldValue;
    if (selectedValue && context.isMounted) {
      await loadValues(selectedValue);
    }
  };
  const onChange = (groupFieldValue: string) => {
    setSearchParams({ "p": groupFieldValue });
  };

  async function loadValues(groupFieldValue: string) {
    const filters = { [groupFieldName]: groupFieldValue };
    const result = await api.query(props.name, filters, statsOptions?.statsFieldName);
    setSelectedGroupFieldValue(groupFieldValue);
    setValues(result.items);
    setStats(result.stats);
  }

  useEffectLoader(loader, onLoad, [props]);

  const Content = () => {
    const footer = statsOptions?.showSum ? { "Total": formatCurrency(stats?.sum) } : {};
    const optionsLabels = options.map((o, index) => (
      <option key={"option-" + index} value={o.groupFieldValue}>{o.transformValue}</option>));
    const rows = values.map((value: any) => ({ spec: asSpecs(spec), payload: value }));
    const actions = createListTableActions(navigateToView, navigateToEdit, onDelete);
    return (
      <>
        <div className="field mb-4">
          <label className="label">{groupFieldLabel}</label>
          <div className="control">
            <div className="select is-fullwidth">
              <select value={selectedGroupFieldValue} onChange={e => onChange(e.target.value)}>{optionsLabels}</select>
            </div>
          </div>
        </div>
        <AckTable rows={rows} footer={footer} actions={actions}/>
      </>
    );
  };
  return (
    <>
      {loader.errors && loader.errors.length > 0 && <AckErrorMessage errors={loader.errors}/>}
      <div className="buttons are-small">
        <button className="button is-primary is-small" onClick={navigateToCreate}>Create</button>
      </div>
      <AckLoader isLoading={loader.isLoading}/>
      {!loader.isLoading && options.length > 0 && <Content/>}
      {!loader.isLoading && options.length === 0 && <Empty/>}
    </>
  );
};

/** The component rendering a single resource. */
export const EmbeddedViewResource: FC<EmbeddedProps & ChangeListener> = (props) => {
  const [spec, setSpec] = useState([] as any[]);
  const [value, setValue] = useState(undefined as any);
  const loader = useLoader();
  const api = useResourceApi(props.apiPath);
  const { id, type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const mode = type === Type.VIEW ? FormModes.READ : FormModes.WRITE;

  const navigateToEdit = () => navigate(editPath(location.pathname, id!, new RegExp(`/${id}\/.*`, "i")));
  const navigateToView = () => navigate(viewPath(location.pathname, id!, new RegExp(`/${id}\/.*`, "i")));
  const navigateToList = () => navigate(listPath(location.pathname, new RegExp(`/${id}\/.*`, "i")));

  const onDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      loader.load(async () => {
        if (props.beforeDelete) {
          await props?.beforeDelete(id);
        }
        await api.deleteValue(props.name, id);
      });
      navigateToList();
    }
  };

  const onEdit = (model: object) => {
    loader.load(async () => {
      const payload = props.beforeEdit ? await props.beforeEdit(model) : model;
      await api.patchValue(props.name, payload, id!);
      navigateToView();
    });
  };

  const onLoad = async (context: any) => {
    const spec = await api.getSpec(props.name);
    const value = await api.getValue(props.name, id!);
    if (context.isMounted) {
      setSpec(spec);
      setValue(value);
    }
  };

  useEffectLoader(loader, onLoad, [props]);

  const Body = () => (<AckModelForm spec={spec} value={value} onSubmit={onEdit} mode={mode}/>);
  const onEditClick = () => navigateToEdit();
  const onViewClick = () => navigateToView();
  const onListClick = () => navigateToList();
  return (
    <>
      {loader.errors && loader.errors.length > 0 && <AckErrorMessage errors={loader.errors}/>}
      <div className="buttons are-small">
        <button className="button is-light" onClick={onListClick}>List</button>
        {type === Type.VIEW && <button className="button is-light" onClick={onEditClick}>Edit</button>}
        {type === Type.EDIT && <button className="button is-light" onClick={onViewClick}>View</button>}
        <button className="button is-outlined is-danger" onClick={() => onDelete(id!)}>Delete</button>
      </div>
      <AckLoader isLoading={loader.isLoading}/>
      {!loader.isLoading && value && <Body/>}
    </>
  );
};

/** The component rendering the creation form for a single resource. */
export const EmbeddedCreateResource: FC<EmbeddedProps & ChangeListener> = (props) => {
  const [spec, setSpec] = useState([] as any[]);
  const loader = useLoader();
  const api = useResourceApi(props.apiPath);
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToList = () => navigate(listPath(location.pathname, new RegExp("/create.*")));

  const onCreate = (model: object) => {
    loader.load(async () => {
      const payload = props.beforeCreate ? await props.beforeCreate(model) : model;
      await api.postValue(props.name, payload);
      navigateToList();
    });
  };

  const onLoad = async (context: any) => {
    const spec = await api.getSpec(props.name);
    if (context.isMounted) {
      setSpec(spec);
    }
  };

  useEffectLoader(loader, onLoad, [props]);

  const Body = () => (<AckModelForm spec={spec} onSubmit={onCreate} mode={FormModes.WRITE}/>);
  return (
    <>
      {loader.errors && loader.errors.length > 0 && <AckErrorMessage errors={loader.errors}/>}
      <div className="buttons are-small">
        <button className="button is-light" onClick={() => navigateToList()}>List</button>
      </div>
      <AckLoader isLoading={loader.isLoading}/>
      {!loader.isLoading && <Body/>}
    </>
  );
};

/** The component encapsulating the different presentation modes for a single resource. */
export const AckEmbeddedResource: FC<EmbeddedProps & OnResourceChangeListener> = (props) => {
  const [state, setState] = useState({ ...props });
  useEffect(() => setState({ ...props }), [props]);

  const ListResource = () => {
    return props.listOptions != null ? (<EmbeddedGroupListResource {...state}/>) : (<EmbeddedListResource {...state}/>);
  };
  return (
    <Routes>
      <Route path="/" element={<ListResource/>}/>
      <Route path="create" element={<EmbeddedCreateResource {...state} />}/>
      <Route path=":id/:type" element={<EmbeddedViewResource {...state} />}/>
    </Routes>
  );
};

/** A decorator that includes authentication checks for the embedded resource. */
export const PrivateAckEmbeddedResource: FC<EmbeddedProps> = (props) => {
  // FIXME: This returnTo is not working as expected
  const location = useLocation();
  const returnTo = () => location.pathname;
  const PrivateResourceRoute = withPrivateRoute(() => (<AckEmbeddedResource {...props}/>), returnTo);
  return (<PrivateResourceRoute/>);
};
